/* eslint-disable prefer-destructuring */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { updateParentRows } from 'src/components/collapsible-table/table-data-prep';
import getDataForGlobalInputChange from '../components/row-calculations-component/data-prep-for-global-input';
import getSelectCheckboxInputChange from '../components/row-calculations-component/data-prep-for-checkbox-input';
import getNumericInputChange from '../components/row-calculations-component/data-prep-for-number-input';
import { getCurrentRowToChangeFromState } from '../utils/table-utils';
import getSelectDropdownInputChange from '../components/row-calculations-component/data-prep-for-dropdown-input';
import {
  ISimResultToggleOption,
  Header,
  ITableData,
  TypeOfTableData,
  ACTION_TYPE
} from '../../../../pricing-simulator/types/generic-table.model';
import {
  ISimTableColumnMetadata,
  IDataRow
} from '../../../../pricing-simulator/types/simulation-view-table.model';
import {
  filterColumnsForToggleView,
  createColumnVisibilityStateForOutputColumns
} from '../../../../pricing-simulator/utils/column-utils';
import { getPricingSimulationData } from '../../../../pricing-simulator/store/pricing-simulator-slice';
import { getSKUDelistData } from '../../../../sku-delist/store/sku-delist-slice';
import { getRenovationTableData } from '../../../../renovation/store/renovation-slice';

const initialTableData = {
  legend: { metadata: [] },
  ownKPIColumnMetadata: {
    inputCols: [],
    resultCols: []
  },
  retailerKPIColumnMetadata: {
    inputCols: [],
    resultCols: []
  },
  metadata: [],
  show_edited: {
    enabled: false,
    label: ''
  },
  row: [],
  toggle: {
    metadata: [],
    data: {}
  },
  output_toggle: {
    metadata: [],
    data: {}
  },
  footer: {
    metadata: {
      enabled: false,
      label: ''
    },
    row: []
  },
  header: []
};
interface ITableState {
  simulationTableMetadata: {
    listPrice: ISimTableColumnMetadata[];
    retailPrice: ISimTableColumnMetadata[];
    retailPriceSimulationResult: ISimTableColumnMetadata[];
    listPriceSimulationResult: ISimTableColumnMetadata[];
    displayColumns: ISimTableColumnMetadata[];
  };
  isSKUEditedClick: boolean;
  toggleLabelname: string;
  outputToggle: string;
  outputToggleOptions: ISimResultToggleOption[];
  inputCols: Header[];
  tableMetadaData?: ITableData;
  baseTableMetadata?: ITableData;
  outputColumnVisibilityState: { [key: string]: boolean };
  resultCols: Header[];
  isFetchingTableData: boolean;
}

const initialState: ITableState = {
  isSKUEditedClick: false,
  outputColumnVisibilityState: {},
  inputCols: [],
  resultCols: [],
  simulationTableMetadata: {
    listPrice: [],
    retailPrice: [],
    retailPriceSimulationResult: [],
    listPriceSimulationResult: [],
    displayColumns: []
  },
  toggleLabelname: '',
  outputToggle: '',
  outputToggleOptions: [],
  tableMetadaData: initialTableData,
  baseTableMetadata: initialTableData,
  isFetchingTableData: true
};

const tableSlice2 = createSlice({
  name: 'table2',
  initialState,
  reducers: {
    setTableMetaData(state, action: PayloadAction<{ tableData: ITableData }>) {
      state.tableMetadaData = action.payload.tableData;
      // setting base table metadata for comparing base with current tableMetaData on change of input cells
      state.baseTableMetadata = action.payload.tableData;
      state.isSKUEditedClick = false;
      state.toggleLabelname = '';
      if (state.tableMetadaData.header) {
        state.resultCols = filterColumnsForToggleView(
          [TypeOfTableData.OUTPUT],
          state.tableMetadaData.header,
          ''
        );
        state.inputCols = filterColumnsForToggleView(
          [
            TypeOfTableData.DISPLAY,
            TypeOfTableData.LEGEND_FLAG,
            TypeOfTableData.INPUT
          ],
          state.tableMetadaData.header,
          ''
        );
      }
      if (action.payload.tableData.show_edited) {
        state.toggleLabelname = action.payload.tableData.show_edited.label;
      }
      if (state.tableMetadaData.output_toggle) {
        state.outputToggleOptions = Object.keys(
          state.tableMetadaData.output_toggle.data || {}
        )
          .map((key) => state.tableMetadaData.output_toggle.data[key])
          .sort((a, b) => a.pk_id - b.pk_id);
        state.outputToggle = state.outputToggleOptions
          .map((val) => val.name)
          .reduce((prev, curr, index) => {
            if (index === 0) {
              return curr;
            }
            return prev;
          }, '');
        state.outputColumnVisibilityState =
          state.tableMetadaData.ownKPIColumnMetadata &&
          state.tableMetadaData.ownKPIColumnMetadata.resultCols.length > 0
            ? createColumnVisibilityStateForOutputColumns(
                [
                  ...state.tableMetadaData.ownKPIColumnMetadata.resultCols,
                  ...state.tableMetadaData.retailerKPIColumnMetadata.resultCols
                ],
                state.outputToggle
              )
            : createColumnVisibilityStateForOutputColumns(
                state.resultCols,
                state.outputToggle
              );
      }
      state.isFetchingTableData = false;
    },
    toggleOutputColumnVisibility(
      state,
      action: PayloadAction<{ toggledView: string }>
    ) {
      state.outputToggle = action.payload.toggledView;
      state.outputColumnVisibilityState =
        state.tableMetadaData.ownKPIColumnMetadata &&
        state.tableMetadaData.ownKPIColumnMetadata.resultCols.length > 0
          ? createColumnVisibilityStateForOutputColumns(
              [
                ...state.tableMetadaData.ownKPIColumnMetadata.resultCols,
                ...state.tableMetadaData.retailerKPIColumnMetadata.resultCols
              ],
              state.outputToggle
            )
          : createColumnVisibilityStateForOutputColumns(
              state.resultCols,
              state.outputToggle
            );
    },
    handleInputLevelToggleChange(
      state,
      action: PayloadAction<{ toggledView: string }>
    ) {
      state.outputToggle = action.payload.toggledView;
      state.outputColumnVisibilityState =
        createColumnVisibilityStateForOutputColumns(
          [...state.inputCols, ...state.resultCols],
          state.outputToggle
        );
    },
    handleGlobalNumericValueInputChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
      }>
    ) {
      const updatedColumnData = getDataForGlobalInputChange(
        state.tableMetadaData,
        ACTION_TYPE.NUMBER,
        action
      );
      state.tableMetadaData.row = updatedColumnData;
    },
    handleGlobalDropdownValueInputChange(
      state,
      action: PayloadAction<{
        columnMetadata: Header;
        newValue: number;
      }>
    ) {
      const updatedColumnData = getDataForGlobalInputChange(
        state.tableMetadaData,
        ACTION_TYPE.DROPDOWN,
        action
      );
      state.tableMetadaData.row = updatedColumnData;
    },
    handleGlobalCheckboxValueInputChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
      }>
    ) {
      const updatedColumnData = getDataForGlobalInputChange(
        state.tableMetadaData,
        ACTION_TYPE.CHECKBOX,
        action
      );
      state.tableMetadaData.row = updatedColumnData;
    },
    handleToggleShowEditedPPGsClick(state) {
      state.isSKUEditedClick = !state.isSKUEditedClick;
    },
    handleValueOfToggleLabelName(
      state,
      action: PayloadAction<{
        toggleLabelname: string;
      }>
    ) {
      state.toggleLabelname = action.payload.toggleLabelname;
    },
    /* 
            Handler for number/Percentage type input value change. 
        */
    handleNumericInputValueChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
        dataRow: IDataRow;
        changedRows: any;
        uniqueIDColumns: Header[];
        depth: number;
      }>
    ) {
      const {
        dataRow,
        newValue,
        changedRows,
        columnMetadata,
        uniqueIDColumns,
        depth
      } = action.payload;

      // filtering id columns ending with "_id"
      const idColumns = uniqueIDColumns.filter((data) =>
        data.parent.parent_field.endsWith('_id')
      );

      const uniqueIDKey = idColumns.length
        ? idColumns[0].parent.parent_field
        : 'ppg_id';
      const rowStateData = state.tableMetadaData.row.find(
        (rowData) => rowData.parent.data[uniqueIDKey] === dataRow[uniqueIDKey]
      );
        const rowToChange = getCurrentRowToChangeFromState(
          depth,
          rowStateData,
          +dataRow.index
        );
      if (changedRows.length) {
        updateParentRows(
          state.tableMetadaData.row,
          changedRows,
          // MODIFIED_ROW_INDICATOR_KEY.RENOVATION_IS_EDITED,
          0,
          dataRow,
          columnMetadata,
          // MODIFIED_ROW_INDICATOR_KEY.RENOVATION_IS_NOT_DEFAULT
          '0'
        );
      } else if (rowToChange?.parent?.data) {
        rowToChange.parent.data[
          // MODIFIED_ROW_INDICATOR_KEY.RENOVATION_IS_EDITED
          '0'
        ] = true;
      } else {
        return;
      }
      getNumericInputChange({
        tableMetaData: state.tableMetadaData,
        columnMetadata,
        rowStateData: rowToChange,
        newValue
      });
    },
    /* 
           Handler for checkbox type input value change.
       */
    handleBooleanInputValueChange(
      state,
      action: PayloadAction<{
        columnMetadata: Header;
        uniqueIDColumns: Header[];
        rowIndices: number[];
        newValue: any;
        dataRow: IDataRow;
        isRowCanExpand: boolean;
        depth: number;
      }>
    ) {
      const {
        dataRow,
        newValue,
        rowIndices,
        columnMetadata,
        uniqueIDColumns,
        depth
      } = action.payload;

      // filtering id columns ending with "_id"
      const idColumns = uniqueIDColumns.filter((data) =>
        data.parent.parent_field.endsWith('_id')
      );

      const uniqueIDKey = idColumns.length
        ? idColumns[0].parent.parent_field
        : 'ppg_id';
      const parentRow = state.tableMetadaData.row.find(
        (rowData) => rowData.parent.data[uniqueIDKey] === dataRow[uniqueIDKey]
      );
      getSelectCheckboxInputChange({
        columnMetadata,
        parentRow,
        newInputValue: newValue,
        rowIndices
      });
    },
    /* 
          Handler for checkbox type input value change.
      */
    handleDropdownInputValueChange(
      state,
      action: PayloadAction<{
        columnMetadata: Header;
        uniqueIDColumns: Header[];
        rowIndices: number[];
        newValue: any;
        dataRow: IDataRow;
        isRowCanExpand: boolean;
        depth: number;
      }>
    ) {
      const {
        dataRow,
        newValue,
        rowIndices,
        columnMetadata,
        uniqueIDColumns,
        depth
      } = action.payload;

      // filtering id columns ending with "_id"
      const idColumns = uniqueIDColumns.filter((data) =>
        data.parent.parent_field.endsWith('_id')
      );

      const uniqueIDKey = idColumns.length
        ? idColumns[0].parent.parent_field
        : 'ppg_id';
      const parentRow = state.tableMetadaData.row.find(
        (rowData) => rowData.parent.data[uniqueIDKey] === dataRow[uniqueIDKey]
      );
      getSelectDropdownInputChange({
        columnMetadata,
        parentRow,
        newInputValue: newValue,
        rowIndices
      });
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getPricingSimulationData.pending, (state) => {
        state.tableMetadaData = initialTableData;
      })
      .addCase(getSKUDelistData.pending, (state) => {
        state.tableMetadaData = initialTableData;
      })
      .addCase(getRenovationTableData.pending, (state) => {
        state.tableMetadaData = initialTableData;
      })
});

export const {
  setTableMetaData,
  handleGlobalNumericValueInputChange,
  handleInputLevelToggleChange,
  handleGlobalCheckboxValueInputChange,
  toggleOutputColumnVisibility,
  handleGlobalDropdownValueInputChange,
  handleToggleShowEditedPPGsClick,
  handleNumericInputValueChange,
  handleBooleanInputValueChange,
  handleDropdownInputValueChange
} = tableSlice2.actions;

export {};

export default tableSlice2;
